import { useEffect, useState } from "react";
import { Tooltip } from "react-tooltip";
import { deepEqual } from "fast-equals";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { clearAccessibility, nullAccessibility, setAccessibility } from "../../app/userSlice";
import AccessibilityIcon from "../../assets/AccessibilityIcon";
import { Popover, PopoverContent, PopoverHeading, PopoverTrigger } from "../../components/Popover";

import { useSaveAccessibilityMutation } from "../users/usersApiSlice";
import {
	accessibilityToAxString,
	applyColorContrast,
	applyFontSize,
	resetAccessibility,
} from "./accessibilityUtil";

import "./accessibility.scss";
import { xSymbolUnicode } from "../../app/utils";

export default function AccessibilityPopover() {
	const [open, setOpen] = useState(false);
	const [axLoaded, setAxLoaded] = useState(false);

	const accessibility = useAppSelector((state) => state.user.accessibility) || nullAccessibility;
	const [savedAccessibility, setSavedAccessibility] = useState(accessibility);

	const dispatch = useAppDispatch();

	const [saveAccessibility] = useSaveAccessibilityMutation();

	useEffect(() => {
		if (open) {
			setSavedAccessibility(accessibility);
			setAxLoaded(true);
		} else if (axLoaded) {
			if (!deepEqual(savedAccessibility, accessibility)) {
				const axString = accessibilityToAxString(accessibility);
				saveAccessibility(axString);
			}
		}
	}, [open]);

	function handleFontSizeChange(value: string | null) {
		const ax = { ...accessibility, fontSize: value };
		applyFontSize(ax.fontSize);
		dispatch(setAccessibility(ax));
	}

	function handleColorContrastChange(value: string | null) {
		const ax = { ...accessibility, colorContrast: value, tintOverlay: null };
		applyColorContrast(ax.colorContrast);
		dispatch(setAccessibility(ax));
	}

	function handleTintOverlayChange(value: string | null) {
		const ax = { ...accessibility, colorContrast: null, tintOverlay: value };
		applyColorContrast(ax.colorContrast);
		dispatch(setAccessibility(ax));
	}

	function handleLineReaderChange(value: string | null) {
		const ax = { ...accessibility, lineReader: value };
		dispatch(setAccessibility(ax));
	}

	function handleReset() {
		resetAccessibility();
		dispatch(clearAccessibility());
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger id="accessibility-button" onClick={() => setOpen((v) => !v)}>
				<div
					data-tooltip-id="accessibility-icon-tooltip"
					className="h-10 w-10 flex justify-center items-center cursor-pointer rounded-full bg-white/10 ring-2 ring-white/10 hover:bg-white/30 hover:ring-white/60 hover:opacity-85 focus:outline-hidden focus:ring-2 focus:ring-white">
					<AccessibilityIcon
						className="h-7 w-7 fill-white"
						aria-label="Accessibility Options"
					/>
				</div>
				<Tooltip
					id="accessibility-icon-tooltip"
					place="bottom-end"
					content="Accessibility"
					className="z-40"></Tooltip>
			</PopoverTrigger>
			<PopoverContent className="popover accessibility-popover bg-white rounded-md border border-gray-200 shadow-md shadow-gray-200 z-[2]">
				<PopoverHeading className="flex justify-between items-center py-2 px-4 bg-gray-100 border-b border-b-gray-200 ">
					<div className="text-base font-bold text-primary-700">
						Accessibility Options
					</div>
					<div className="cursor-pointer" onClick={() => setOpen(false)}>
						{xSymbolUnicode}
					</div>
				</PopoverHeading>
				<div className="p-4">
					<AccessibilitySection
						title="Font Sizes"
						type="font-size"
						value={accessibility.fontSize}
						onChange={handleFontSizeChange}
						className="px-10"
					/>
					<hr className="my-2" />
					<AccessibilitySection
						title="Color Contrast"
						type="contrast"
						value={accessibility.colorContrast}
						onChange={handleColorContrastChange}
					/>
					<hr className="my-2" />
					<AccessibilitySection
						title="Tint Overlay"
						type="overlay"
						value={accessibility.tintOverlay}
						onChange={handleTintOverlayChange}
					/>
					<hr className="my-2" />
					<div className="btn-group flex justify-between px-10 pt-2" id="extras">
						<div className="pr-4">
							<AccessibilitySection
								title="Line Reader"
								type="line-reader"
								value={accessibility.lineReader}
								onChange={handleLineReaderChange}
							/>
						</div>
						<div className="pl-4">
							<AccessibilitySection
								title="Reset"
								type="reset"
								value=""
								onChange={handleReset}
							/>
						</div>
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
}

interface AccessibilityButtonConfig {
	id: number;
	type: string;
	value: string;
	title: string;
	icon: string;
}

const accessibilityConfig: AccessibilityButtonConfig[] = [
	{
		id: 110,
		type: "font-size",
		value: "medium",
		title: "Large Font Size",
		icon: "icon-font-medium",
	},
	{
		id: 120,
		type: "font-size",
		value: "large",
		title: "Huge Font Size",
		icon: "icon-font-large",
	},
	{
		id: 200,
		type: "contrast",
		value: "blackOnCream",
		title: "Black on Cream",
		icon: "icon-black-cream",
	},
	{
		id: 210,
		type: "contrast",
		value: "blackOnLightBlue",
		title: "Black on Light Blue",
		icon: "icon-black-light-blue",
	},
	{
		id: 220,
		type: "contrast",
		value: "blackOnLightMagenta",
		title: "Black on Light Magenta",
		icon: "icon-black-light-magenta",
	},
	{
		id: 230,
		type: "contrast",
		value: "whiteOnBlack",
		title: "White on Black",
		icon: "icon-white-black",
	},
	{
		id: 240,
		type: "contrast",
		value: "yellowOnDarkBlue",
		title: "Yellow on Dark Blue",
		icon: "icon-yellow-dark-blue",
	},
	{
		id: 300,
		type: "overlay",
		value: "pink",
		title: "Pink Tint",
		icon: "icon-pink-overlay",
	},
	{
		id: 310,
		type: "overlay",
		value: "green",
		title: "Green Tint",
		icon: "icon-green-overlay",
	},
	{
		id: 320,
		type: "overlay",
		value: "aqua",
		title: "Aqua Tint",
		icon: "icon-aqua-overlay",
	},
	{
		id: 330,
		type: "overlay",
		value: "blue",
		title: "Blue Tine",
		icon: "icon-blue-overlay",
	},
	{
		id: 340,
		type: "overlay",
		value: "yellow",
		title: "Yellow Tint",
		icon: "icon-yellow-overlay",
	},
	{
		id: 350,
		type: "line-reader",
		value: "enabled",
		title: "Line Reader",
		icon: "icon-line-reader",
	},
	{
		id: 360,
		type: "reset",
		value: "reset",
		title: "Reset All",
		icon: "icon-reset",
	},
];

interface AccessibilitySectionProps {
	title: string;
	type: string;
	value: string | null;
	className?: string;
	onChange: (value: string | null) => void;
}

function AccessibilitySection(props: AccessibilitySectionProps) {
	function handleClick(e: React.MouseEvent<HTMLElement>) {
		e.preventDefault();
		if (e.currentTarget.classList.contains("selected")) {
			props.onChange(null);
		} else {
			const toolValue = e.currentTarget.dataset.accessibilityValue ?? "";
			props.onChange(toolValue);
		}
	}

	return (
		<section>
			<h3 className="text-center text-gray-600 font-bold text-base">{props.title}</h3>
			<div
				className={`${props.className} btn-group flex justify-between accessibility-options`}
				id={props.type}>
				{accessibilityConfig
					.filter((config) => config.type == props.type)
					.map((config) => (
						<AccessibilityButton
							key={config.id}
							config={config}
							value={props.value}
							onClick={handleClick}
						/>
					))}
			</div>
		</section>
	);
}

interface AccessibilityButtonProps {
	config: AccessibilityButtonConfig;
	value: string | null;
	onClick: (e: React.MouseEvent<HTMLElement>) => void;
}

function AccessibilityButton({ config, value, onClick }: AccessibilityButtonProps) {
	return (
		<button
			className={`btn ${value === config.value ? "selected" : ""}`}
			data-accessibility={config.type}
			data-accessibility-value={config.value}
			title={config.title}
			onClick={onClick}>
			<span className={`${config.icon} accessibility-sprites`}></span>
		</button>
	);
}
